import {
  createEstablishedAhpPendingMessage,
  type ContributedInstructionsMetadata,
  type EstablishedAhpSession,
} from '@github-ui/ahp-session-client/contracts'
import {TERMINAL_SESSION_STATES, type Session} from '../../../types/session'
import {LoadingMessage} from '../LoadingMessage'
import {AhpSessionHistoryTimeline} from './AhpSessionHistoryTimeline'
import {AhpTimelineView, type TimelineMarker} from './AhpTimelineView'
import {
  OptionalEstablishedAhpSessionTimeline,
  type EstablishedAhpSessionTimelineProps,
} from './EstablishedAhpSessionTimeline'
import {useTaskStartupTimeline} from './use-task-startup-timeline'
import {useTaskAhpHistory} from '../../../hooks/use-task-ahp-history'
import {isConversationRefused} from '../../../utils/conversation-access'
import {useReportConversationRefused} from '../../../contexts/ConversationAccessContext'
import {ConversationLocked} from '../../conversation-access/ConversationLocked'
import {useState} from 'react'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import type {APITask} from '../../../types/api-task'
import type {PublicUser} from '../../../types/public-user'
import {getChannelOrigin} from '../../../types/origin'
import {ChannelSessionAttribution} from './ChannelSessionAttribution'
import {useTaskHistoryRequired} from '../../../contexts/new-task-context'

const emptyProfiles: ReadonlyMap<number, PublicUser> = new Map()

interface AhpSessionTimelineProps {
  session: Session
  creatorType?: APITask['creator_type']
  establishedAhpSession?: EstablishedAhpSession
  establishedAhpSessionRequired?: boolean
  eligibilityPending: boolean
  wakeable: boolean
  suppressInitialUserMessage?: boolean
  timelineMarkers?: TimelineMarker[]
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
  preserveHistory?: boolean
  queueReadOnly?: boolean
  loadingPresentation?: 'local' | 'page'
}

export function AhpSessionTimeline({creatorType, ...props}: AhpSessionTimelineProps) {
  if (!getChannelOrigin(props.session.event_type)) return <AhpSessionTimelineContent {...props} />

  return (
    <>
      <ChannelSessionAttribution
        session={props.session}
        chatState={undefined}
        profiles={emptyProfiles}
        creatorType={creatorType}
      />
      <AhpSessionTimelineContent {...props} />
    </>
  )
}

function AhpSessionTimelineContent({
  session,
  establishedAhpSession,
  establishedAhpSessionRequired = false,
  eligibilityPending,
  wakeable,
  suppressInitialUserMessage = false,
  timelineMarkers,
  onContributedInstructions,
  preserveHistory: requestedHistory,
  queueReadOnly,
  loadingPresentation = 'local',
}: Omit<AhpSessionTimelineProps, 'creatorType'>) {
  const {taskStartup, cloneProgress, provisioningMessage, reportedError, provisioning} = useTaskStartupTimeline(
    session.task_id,
  )
  const {models} = useAgentModels()
  const historyRequired = useTaskHistoryRequired(session.task_id)
  const preserveHistory = requestedHistory && historyRequired

  if (establishedAhpSession || (preserveHistory && !taskStartup) || !historyRequired) {
    return (
      <HistoryAwareSessionTimeline
        session={session}
        availableModels={models}
        establishedAhpSession={establishedAhpSession}
        historyEligibilityPending={eligibilityPending}
        preserveHistory={preserveHistory && !taskStartup}
        historySkipped={!historyRequired}
        awaitingLiveSession={establishedAhpSessionRequired || eligibilityPending || !!taskStartup}
        suppressInitialUserMessage={suppressInitialUserMessage}
        timelineMarkers={timelineMarkers}
        additionalErrorMessage={!taskStartup ? reportedError : undefined}
        wakeable={wakeable}
        fallback={
          historyRequired ? (
            <AhpSessionHistoryTimeline
              session={session}
              onContributedInstructions={onContributedInstructions}
              loadingPresentation={loadingPresentation}
            />
          ) : undefined
        }
        taskStartup={taskStartup}
        taskStartupCloneProgress={cloneProgress}
        fallbackProvisioning={provisioning}
        onContributedInstructions={onContributedInstructions}
        onCancelQueuedMessage={
          establishedAhpSession && !queueReadOnly ? id => establishedAhpSession.input.removeQueued(id) : undefined
        }
        onSendNowQueuedMessage={
          establishedAhpSession && !queueReadOnly
            ? async message => {
                await establishedAhpSession.input.sendNow(createEstablishedAhpPendingMessage(message))
              }
            : undefined
        }
        loadingPresentation={loadingPresentation}
      />
    )
  }

  if (taskStartup) {
    return (
      <AhpTimelineView
        state={undefined}
        view="loading"
        taskStartup={taskStartup}
        taskStartupCloneProgress={cloneProgress}
        loadingPresentation={loadingPresentation}
        loadingSourceKind="preparation"
      />
    )
  }

  if (provisioningMessage)
    return loadingPresentation === 'page' ? (
      <AhpTimelineView
        state={undefined}
        view="loading"
        provisioningMessage={provisioningMessage}
        provisioning={provisioning}
        loadingPresentation="page"
        loadingSourceKind="preparation"
      />
    ) : (
      <LoadingMessage message={provisioningMessage} />
    )
  if (establishedAhpSessionRequired || !eligibilityPending) {
    return (
      <AhpSessionHistoryTimeline
        session={session}
        onContributedInstructions={onContributedInstructions}
        loadingPresentation={loadingPresentation}
      />
    )
  }

  return loadingPresentation === 'page' ? (
    <AhpTimelineView state={undefined} view="loading" loadingPresentation="page" loadingSourceKind="history" />
  ) : (
    <LoadingMessage message="Loading…" />
  )
}

function HistoryAwareSessionTimeline({
  session,
  establishedAhpSession,
  historyEligibilityPending,
  preserveHistory = false,
  historySkipped = false,
  awaitingLiveSession = false,
  ...props
}: EstablishedAhpSessionTimelineProps & {
  session: Session
  establishedAhpSession?: EstablishedAhpSession
  historyEligibilityPending: boolean
  historySkipped?: boolean
  awaitingLiveSession?: boolean
}) {
  // Select the history-capable renderer before eligibility resolves so the focused timeline subtree is never replaced.
  const [useHistoryTimeline] = useState(preserveHistory || historyEligibilityPending)
  const history = useTaskAhpHistory(session.task_id, undefined, preserveHistory)
  const refused = !historySkipped && isConversationRefused(history.error)
  useReportConversationRefused(session.task_id, refused)
  if (refused) return <ConversationLocked surface="ahp-timeline" onRetry={() => void history.refetch()} />
  const bundle = preserveHistory ? history.data?.find(saved => saved.sessionId === session.id)?.bundle : undefined
  const liveSessionUnavailable = historySkipped && !establishedAhpSession && !awaitingLiveSession
  const fallbackView = liveSessionUnavailable
    ? 'error'
    : history.isPending && !historySkipped
      ? 'loading'
      : history.isError && !historySkipped
        ? 'error'
        : !establishedAhpSession && !TERMINAL_SESSION_STATES.has(session.state)
          ? props.loadingPresentation === 'page'
            ? 'ready'
            : 'loading'
          : 'ready'

  return (
    <OptionalEstablishedAhpSessionTimeline
      {...props}
      fallbackView={fallbackView}
      fallbackErrorMessage={
        liveSessionUnavailable ? 'The live conversation is unavailable. Reconnect to continue.' : history.error?.message
      }
      preserveHistory={useHistoryTimeline}
      historyBundle={bundle}
      fallback={preserveHistory ? undefined : props.fallback}
      additionalErrorMessage={
        establishedAhpSession && preserveHistory
          ? (history.error?.message ?? props.additionalErrorMessage)
          : props.additionalErrorMessage
      }
      loadingPresentation={props.loadingPresentation}
    />
  )
}
